
import React, { useEffect, useState, useMemo } from "react";
import { useLoader } from '../../../../context/LoaderProvider.jsx';
import {
    refundApi,
    refundDataProcessor,
    refundDateFormatter,
    refundExport,
    refundUIConstants
} from '../../../Servicefile/Apiservice.js';

const Cancelandrefundpage = () => {
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { showLoader, hideLoader } = useLoader();

    // Filter states
    const [searchType, setSearchType] = useState("userId");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
    const [filterDate, setFilterDate] = useState("");

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedRefund, setSelectedRefund] = useState(null);

    // Fetch refund history
    const fetchRefundHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            showLoader();
            const data = await refundApi.getAllRefundHistory();

            if (Array.isArray(data)) {
                const filteredPayments = refundDataProcessor.filterPaymentMethods(data);
                const dataWithSno = refundDataProcessor.addSerialNumbers(filteredPayments);
                setAllData(dataWithSno);
                setFilteredData(dataWithSno);
            } else {
                throw new Error("Unexpected response format");
            }
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Failed to fetch data.");
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    useEffect(() => {
        fetchRefundHistory();
    }, []);

    // Apply filters
    const searchFilteredData = useMemo(() => {
        return refundDataProcessor.filterData(
            allData, searchType, searchQuery, selectedStatusFilter, filterDate
        );
    }, [allData, searchType, searchQuery, selectedStatusFilter, filterDate]);

    useEffect(() => {
        setFilteredData(searchFilteredData);
    }, [searchFilteredData]);

    const uniqueStatuses = useMemo(
        () => refundDataProcessor.getUniqueStatuses(allData),
        [allData]
    );

    const clearAllFilters = () => {
        setSearchQuery("");
        setSelectedStatusFilter("");
        setFilterDate("");
    };

    const handleApprove = async (refundId) => {
        try {
            const data = await refundApi.getRefundByOrderId(refundId);

            const refundDetails = data?.refundId ? data :
                (Array.isArray(data) && data[0]) ||
                data?.data || null;

            if (!refundDetails) throw new Error("Refund details not found.");

            setSelectedRefund(refundDetails);
            setIsPopupOpen(true);
        } catch (err) {
            console.error(err);
            alert("Could not fetch refund details.");
        }
    };

    const closePopup = () => {
        setIsPopupOpen(false);
        setSelectedRefund(null);
    };

    const handleSaveRefund = async () => {
        if (!selectedRefund?.transactionId || !selectedRefund?.price) {
            alert("Missing transaction ID or amount.");
            return;
        }

        try {
            if (selectedRefund.paymentMethod === "Online_Payment") {
                await refundApi.processOnlineRefund(selectedRefund.transactionId);
            } else if (selectedRefund.paymentMethod === "Maamaas_Wallet") {
                alert("Maamaas Wallet refund logic would be implemented here");
            }

            await refundApi.updateRefundStatus(selectedRefund.refundId, "REFUNDED");

            alert("✅ Refund processed successfully!");
            closePopup();
            fetchRefundHistory();
        } catch (err) {
            console.error(err);
            alert("Something went wrong.");
        }
    };

    const handleExport = () => {
        const exportData = filteredData.length > 0 ? filteredData : allData;
        refundExport.exportToCSV(exportData);
    };

    return (
        <div className="container-fluid d-flex flex-column">
            {/* Filters & Export */}
            <div className="row flex-grow-1" style={{ marginTop: "10px" }}>
                <div className="col-lg-12 col-12 ps-2 pe-2">
                    <div className="vh-100" style={{ scrollbarWidth: "none" }}>
                        {/* Filter Controls */}
                        <div className="row g-2 mb-1 align-items-center me-0">
                            <div className="col-md-auto col-12">
                                <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                    <div style={{ minWidth: '150px' }}>
                                        <select
                                            className="form-select"
                                            value={searchType}
                                            onChange={(e) => setSearchType(e.target.value)}
                                            style={{ height: "40px" }}
                                        >
                                            {refundUIConstants.searchOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div style={{ minWidth: '200px' }}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={refundUIConstants.getPlaceholderText(searchType)}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{ height: "40px" }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-auto col-12">
                                <select
                                    className="form-select"
                                    value={selectedStatusFilter}
                                    onChange={(e) => setSelectedStatusFilter(e.target.value)}
                                    style={{ maxWidth: '150px', height: "40px" }}
                                >
                                    <option value="">All Status</option>
                                    {uniqueStatuses.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-auto col-12">
                                <input
                                    className="form-control"
                                    type="date"
                                    value={refundDateFormatter.formatDateForInput(filterDate)}
                                    onChange={(e) => setFilterDate(refundDateFormatter.formatDateForDisplay(e.target.value))}
                                    style={{ height: "40px", minWidth: '150px' }}
                                />
                            </div>

                            <div className="col-md-auto col-12">
                                <button
                                    className="btn"
                                    onClick={clearAllFilters}
                                    style={{
                                        backgroundColor: '#6c757d',
                                        color: '#fff',
                                        height: "40px",
                                        padding: "0 15px"
                                    }}
                                >
                                    Clear Filters
                                </button>
                            </div>

                            <div className="col-md col-12">
                                <div className="d-flex justify-content-end">
                                    <button
                                        onClick={handleExport}
                                        className="btn"
                                        style={{
                                            backgroundColor: 'rgb(230 109 51)',
                                            color: '#fff',
                                            height: "40px",
                                            padding: "0 20px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Export CSV
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Table */}
                        {loading && <p className="text-center mt-5">Loading data...</p>}
                        {error && <p className="text-center text-danger mt-5">{error}</p>}
                        {!loading && !error && (
                            <div
                                className="table-responsive shadow-sm"
                                style={{ height: "60vh", overflowY: "auto", marginTop: "10px" }}
                            >
                                <table className="table-striped table-bordered table-hover align-middle">
                                    <thead style={{ backgroundColor: "rgb(230 109 51)", color: "#fff" }}>
                                        <tr>
                                            <th className="p-2 text-center">SNO</th>
                                            <th className="p-2 text-center">Order ID</th>
                                            <th className="p-2 text-center">User ID</th>
                                            <th className="p-2 text-center">Transaction Id</th>
                                            <th className="p-2 text-center">Name</th>
                                            <th className="p-2 text-center">Request Date</th>
                                            <th className="p-2 text-center">Request Time</th>
                                            <th className="p-2 text-center">Amount</th>
                                            <th className="p-2 text-center">Refund ID</th>
                                            <th className="p-2 text-center">Payment</th>
                                            <th className="p-2 text-center">Description</th>
                                            <th className="p-2 text-center">Status</th>
                                            <th className="text-center p-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.length > 0 ? (
                                            filteredData.map((item, index) => (
                                                <tr key={item.refundId}>
                                                    <td style={refundUIConstants.tableStyles.td}>{item.sno || index + 1}</td>
                                                    <td style={refundUIConstants.tableStyles.td}>{item.orderId}</td>
                                                    <td style={refundUIConstants.tableStyles.td}>{item.userId}</td>
                                                    <td style={refundUIConstants.tableStyles.td}>{item.transactionId}</td>
                                                    <td style={refundUIConstants.tableStyles.td}>{item.userName}</td>
                                                    <td style={refundUIConstants.tableStyles.td}>
                                                        {refundDateFormatter.formatDateForDisplay(item.cancelledAt)}
                                                    </td>
                                                    <td style={refundUIConstants.tableStyles.td}>
                                                        {refundDateFormatter.formatTimeForDisplay(item.cancelledAt)}
                                                    </td>
                                                    <td style={refundUIConstants.tableStyles.td}>₹{Number(item.price || 0).toFixed(2)}</td>
                                                    <td style={refundUIConstants.tableStyles.td}>{item.refundId}</td>
                                                    <td style={refundUIConstants.tableStyles.td}>{item.paymentMethod}</td>
                                                    <td style={refundUIConstants.tableStyles.td}>Refund Requested</td>
                                                    <td style={refundUIConstants.tableStyles.td}>{item.status}</td>
                                                    <td className="p-3">
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => handleApprove(item.refundId)}
                                                            disabled={item.refundStatus === "REFUNDED"}
                                                        >
                                                            {item.refundStatus === "REFUNDED" ? "Refunded" : "Approve"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="13" style={{
                                                    fontStyle: "italic",
                                                    textAlign: "center",
                                                    padding: "20px",
                                                    color: "#888"
                                                }}>
                                                    {allData.length === 0
                                                        ? "Loading data or no refunds found."
                                                        : "No refunds found with the specified filters."}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Refund Popup */}
            {isPopupOpen && selectedRefund && (
                <div style={{
                    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                    background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center",
                    justifyContent: "center", zIndex: 9999, padding: "20px"
                }}>
                    <div style={{
                        background: "#fff", padding: "20px", borderRadius: "12px",
                        width: "100%", maxWidth: "500px", maxHeight: "90vh", overflowY: "auto",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.25)"
                    }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                            <div>
                                <label style={{ fontWeight: "500" }}>Refund ID:</label>
                                <input type="text" value={selectedRefund.refundId || ""} readOnly style={refundUIConstants.tableStyles.input} />
                            </div>
                            <div>
                                <label style={{ fontWeight: "500" }}>Order ID:</label>
                                <input type="text" value={selectedRefund.orderId || ""} readOnly style={refundUIConstants.tableStyles.input} />
                            </div>
                            <div>
                                <label style={{ fontWeight: "500" }}>User Name:</label>
                                <input type="text" value={selectedRefund.userName || ""} readOnly style={refundUIConstants.tableStyles.input} />
                            </div>
                            <div>
                                <label style={{ fontWeight: "500" }}>Price:</label>
                                <input type="text" value={`₹${selectedRefund.price || "0.00"}`} readOnly style={refundUIConstants.tableStyles.input} />
                            </div>
                            <div>
                                <label style={{ fontWeight: "500" }}>Cancelled At:</label>
                                <input type="text" value={refundDateFormatter.formatDateTimeForDisplay(selectedRefund.cancelledAt)} readOnly style={refundUIConstants.tableStyles.input} />
                            </div>
                            <div>
                                <label style={{ fontWeight: "500" }}>Transaction ID:</label>
                                <input type="text" value={selectedRefund.transactionId || ""} readOnly style={refundUIConstants.tableStyles.input} />
                            </div>
                            <div>
                                <label style={{ fontWeight: "500" }}>Payment Method:</label>
                                <input type="text" value={selectedRefund.paymentMethod || ""} readOnly style={refundUIConstants.tableStyles.input} />
                            </div>
                        </div>
                        <div style={{ textAlign: "center", marginTop: "20px" }}>
                            <button onClick={closePopup} style={{
                                backgroundColor: "#d63031", color: "#fff", border: "none",
                                padding: "10px 20px", borderRadius: "6px", cursor: "pointer",
                                fontWeight: "bold", marginRight: "10px"
                            }}>Close</button>
                            <button onClick={handleSaveRefund} style={{
                                backgroundColor: "#00b894", color: "#fff", border: "none",
                                padding: "10px 20px", borderRadius: "6px", cursor: "pointer",
                                fontWeight: "bold"
                            }}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cancelandrefundpage;